import { Link } from "wouter";
import { Check, Receipt, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { IconBadge } from "@/components/waggle/PageShell";
import { useUpgradeDialog } from "@/context/UpgradeContext";
import { useAuth } from "@/context/AuthContext";
import { PLANS, formatShortDate, useCheckoutReturn, useInvoices, usePlan } from "@/lib/plan";

// Settings -> Billing. Plan and usage come from waggle-api; invoices are read
// live from Stripe (sandbox).
export function BillingSection() {
  const { user } = useAuth();
  const { plan, used, limit, remaining, isUnlimited, resetsAt, cancelAtPeriodEnd, currentPeriodEnd } = usePlan();
  const invoices = useInvoices();
  useCheckoutReturn();
  const { openUpgrade } = useUpgradeDialog();
  const current = PLANS.find((p) => p.id === plan)!;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card className="shadow-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              Current plan
              <Badge variant="outline" className="bg-accent text-accent-foreground">
                {current.name}
              </Badge>
            </CardTitle>
            <CardDescription>
              <span className="text-2xl font-semibold text-foreground">{current.price}</span>{" "}
              <span className="text-sm">{current.cadence}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <ul className="flex flex-col gap-2">
              {current.features.map((f) => (
                <li key={f} className="flex gap-2 text-sm text-muted-foreground">
                  <Check aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  {f}
                </li>
              ))}
            </ul>
            {isUnlimited && currentPeriodEnd && (
              <p className="text-sm text-muted-foreground">
                {cancelAtPeriodEnd ? "Ends" : "Renews"} {formatShortDate(currentPeriodEnd, { month: "short", day: "numeric", year: "numeric" })}
              </p>
            )}
            {isUnlimited && (
              <p className="text-xs text-muted-foreground" data-testid="billing-close-account-hint">
                To close your account instead, see{" "}
                <Link href="/settings/security" className="text-primary hover:underline">
                  Sign-in and security
                </Link>
                .
              </p>
            )}
            {!isUnlimited && (
              <Button className="self-start" onClick={openUpgrade} data-testid="button-account-upgrade">
                Upgrade to Pro <Zap />
              </Button>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-none">
          <CardHeader>
            <CardTitle className="text-base">This week's usage</CardTitle>
            <CardDescription>Resets {formatShortDate(resetsAt, { weekday: "long", month: "short", day: "numeric" })}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {isUnlimited ? (
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{used}</span> AI actions used — no limit on Pro
              </p>
            ) : (
              <>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-semibold">AI actions</span>
                  <span className="tabular-nums text-muted-foreground">
                    <span className="font-semibold text-foreground">{used}</span> of {limit} used · {remaining} left
                  </span>
                </div>
                <Progress value={Math.min(100, (used / limit) * 100)} aria-label="AI actions used this week" className="h-2 bg-border" />
              </>
            )}
            <p className="text-xs text-muted-foreground">Each question created or regenerated counts as one AI action.</p>
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-none">
        <CardHeader>
          <CardTitle className="text-base">Payment history</CardTitle>
          {user && <CardDescription>Receipts are sent to {user.email}.</CardDescription>}
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="flex flex-col items-center gap-4 rounded-xl border border-dashed px-6 py-10 text-center">
              <IconBadge tone="muted">
                <Receipt />
              </IconBadge>
              <div>
                <p className="text-sm font-semibold">No payments yet</p>
                <p className="mt-1 text-sm text-muted-foreground">No payment method on file. You won't be charged on the Free plan.</p>
              </div>
            </div>
          ) : (
            <ul className="divide-y">
              {invoices.map((inv) => (
                <li key={inv.id} className="flex items-center justify-between py-3 text-sm">
                  <span>
                    <span className="font-medium">{inv.description}</span>
                    <span className="ml-2 text-muted-foreground">{formatShortDate(inv.date)}</span>
                  </span>
                  <span className="flex items-center gap-3 tabular-nums">
                    {inv.amount.toLocaleString("en-US", { style: "currency", currency: inv.currency.toUpperCase() })}
                    {inv.hostedUrl && (
                      <a href={inv.hostedUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                        View
                      </a>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
